import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { CheckCircle, Edit, Save, UserPlus, X, XCircle } from "lucide-react"; // Added Trash icon  
import { useEffect, useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { app } from "../../firebase";
import { useToast } from "../../hooks/use-toast";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../../components/ui/pagination";
import { Link } from "react-router-dom";

const ManageAccessRoles = ({ user = null }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isMasterAdmin, setIsMasterAdmin] = useState(user?.role === "MASTERADMIN");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toast } = useToast();
  const [users, setUsers] = useState<any[]>([]); // State to hold user data   
  const [roles, setRoles] = useState([]);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [useIdToDelete, setUserIdtoDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const studentsPerPage = 12;

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Fetch all users from Firestore  
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, "users");
        const snapshot = await getDocs(usersCollection);
        const fetchedUsers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as any[];
        // Eliminate duplicates based on emailId  
        const uniqueUsersMap = new Map();
        fetchedUsers.forEach((user) => {
          if (!uniqueUsersMap.has(user.emailId)) {
            uniqueUsersMap.set(user.emailId, user);
          }
        });

        const uniqueUsers = Array.from(uniqueUsersMap.values());
        setUsers(uniqueUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchRoles = async () => {
      try {
        const rolesCollection = collection(db, "roles");
        const snapshot = await getDocs(rolesCollection);
        const fetchedRoles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const rolesArray = fetchedRoles
          .filter((role: any) => isMasterAdmin || role.role !== "MASTERADMIN")
          .map((role: any) => role.role)
          .sort();
        setRoles(rolesArray);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
    fetchUsers();
  }, [db, isMasterAdmin]);

  // Filter users based on search term  
  const filteredUsers = users.filter(
    (user) =>
      user?.fullName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      user?.emailId?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      user?.role?.toLowerCase().includes(searchTerm?.toLowerCase())
  );


  // Then calculate pagination values
  const totalPages = Math.ceil(filteredUsers.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;

  // Get the current page of students
  const currentStudents = filteredUsers.slice(indexOfFirstStudent, indexOfLastStudent);

  const handleEditClick = (user: any) => {
    setEditingId(user.id);
    setEditDialogOpen(true);
    setEditFormData({ ...user });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData(null);
    setEditDialogOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!editFormData) return;
    await updateUser(editFormData?.id ?? editingId, editFormData); // Wrap with await  
    setUsers(users.map(user => user.id === editingId ? editFormData : user));
    setEditingId(null);
    setEditFormData(null);
    setEditDialogOpen(false);

    toast({
      title: "User updated",
      description: "The user's access details have been successfully updated.",
    });
  };

  const updateUser = async (id: string, data: any) => {
    await updateDoc(doc(db, "users", id), data);
  };

  const handleFormChange = (field: keyof any, value: any) => {
    if (editFormData) {
      setEditFormData({ ...editFormData, [field]: value });
    }
  };

  const handleToggleStatus = (id: string) => {
    setUsers(users.map(user => {
      if (user.id === id) {
        const newStatus = user.status === 'active' ? 'inactive' : 'active';
        return { ...user, status: newStatus };
      }
      return user;
    }));
    const user = users.find(u => u.id === id);
    updateUser(id, { ...user, status: users.find(u => u.id === id)?.status === 'active' ? 'inactive' : 'active' });
    toast({
      title: "Status updated",
      description: `User status has been ${users.find(u => u.id === id)?.status === 'active' ? 'deactivated' : 'activated'}.`,
    });
  };

  const handleDeleteUser = async (id: string) => {
    setUserIdtoDelete(id);
    setIsDeleteDialogOpen(true);

  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogOpen(false);
    try {
      // Get the user to delete from Firestore  
      const userDoc = await getDoc(doc(db, "users", useIdToDelete));
      if (!userDoc.exists()) {
        throw new Error("User not found in Firestore");
      }
      const { emailId } = userDoc.data();
      const userAuth = getAuth();
      await deleteDoc(doc(db, "users", useIdToDelete));
      setUsers(users.filter(user => user.id !== useIdToDelete)); // Update state to remove user  

      toast({
        title: "User deleted",
        description: "The user has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error deleting user",
        description: "There was an issue deleting the user: " + error.message,
      });
    }
  };

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="container py-3 space-y-6">
      <div className="flex justify-between items-center pr-2">
        <h1 className="text-2xl font-bold ml-2">Manage Access & Roles</h1>
        <Link to="/admin/create-user">  <Button><UserPlus className="mr-2 h-4 w-4" />User</Button></Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User Accounts</CardTitle>
          <CardDescription>
            Manage user accounts and their access roles
          </CardDescription>
          <div className="mt-4">
            <Input
              placeholder="Search by name, email, role, or UID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>UID</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentStudents.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user?.fullName}</TableCell>
                  <TableCell>{user.emailId}</TableCell>
                  {/* <TableCell className="font-mono text-sm">{user.uid}</TableCell> */}
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {user.status === "active" ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user?.uid ? (
                      <div className="bg-green-500 text-white rounded-full inline-flex items-center justify-center"> {/* Green background */}
                        <CheckCircle size={20} />
                      </div>
                    ) : (
                      <div className="bg-gray-400 text-white rounded-full inline-flex items-center justify-center"> {/* Gray background */}
                        <XCircle size={20} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditClick(user)}><Edit size={16} className="mr-1" /> Edit</Button>
                      <Button
                        variant={user.status === "active" ? "destructive" : "default"}
                        size="sm"
                        onClick={() => handleToggleStatus(user.id)}
                      >
                        {user.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredUsers.length > 0 && (
            <div className="py-4 flex items-center justify-between border-t px-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{indexOfFirstStudent + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastStudent, filteredUsers.length)}
                </span>{" "}
                of <span className="font-medium">{filteredUsers.length}</span> students
              </div>

              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(currentPage - 1)}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum = currentPage;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <PaginationItem key={i} className="cursor-pointer">
                        <PaginationLink
                          onClick={() => handlePageChange(pageNum)}
                          isActive={pageNum === currentPage}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationLink onClick={() => handlePageChange(totalPages)}>
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(currentPage + 1)}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" style={{ maxHeight: '95vh', marginBottom: '0' }}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>

          {editFormData && (
            <div className="grid gap-1 py-1">
              <div>
                <label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <Input
                  id="name"
                  value={editFormData.fullName}
                  onChange={(e) => handleFormChange('fullName', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={editFormData.emailId}
                  onChange={(e) => handleFormChange('emailId', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <label htmlFor="uid" className="text-sm font-medium text-gray-700">
                  UID
                </label>
                <Input
                  id="uid"
                  value={editFormData.uid}
                  onChange={(e) => handleFormChange('uid', e.target.value)}
                  className="mt-1 font-mono"
                />
              </div>

              <div>
                <label htmlFor="role" className="text-sm font-medium text-gray-700">
                  Role
                </label>
                <Select
                  value={editFormData.role}
                  onValueChange={(value) => handleFormChange('role', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RECEPTIONIST">RECEPTIONIST</SelectItem>
                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                    <SelectItem value="STUDENT">STUDENT</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <label htmlFor="status" className="text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <p className="text-xs text-gray-500">
                    Toggle to enable or disable user access
                  </p>
                </div>
                <Switch
                  id="status"
                  checked={editFormData.status === 'active'}
                  onCheckedChange={(checked) =>
                    handleFormChange('status', checked ? 'active' : 'inactive')
                  }
                />
              </div>

              <div className="bg-gray-50 p-1 rounded-md mt-1">
                <p className="text-xs text-gray-500">
                  ID: <span className="font-mono">{editFormData.id}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Last login: {editFormData.lastLogin
                    ? editFormData.lastLogin.toLocaleDateString() + " " + editFormData.lastLogin.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : "Never"
                  }
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="sm:justify-end">
            <Button variant="outline" onClick={handleCancelEdit}>
              <X size={16} className="mr-1" /> Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <Save size={16} className="mr-1" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAccessRoles;  